/* ZLN Theme Core V1 — progressive storefront behavior */
(function () {
  const doc = document;
  const root = doc.documentElement;
  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = doc.body?.dataset.currency || 'HUF';
  const locale = root.lang || 'hu-HU';

  root.classList.remove('no-js');
  root.classList.add('js');

  function announce(message) {
    const status = doc.querySelector('[data-global-status]');
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'HUF' ? 0 : 2
      }).format(amount);
    } catch (error) {
      return amount.toLocaleString(locale);
    }
  }

  function getDrawer() {
    return doc.querySelector('[data-cart-drawer]');
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;

    drawer.hidden = false;
    root.classList.add('cart-drawer-open');
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer.querySelector('.cart-drawer__panel')?.focus();
    });

    doc.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'true');
    });

    return true;
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    root.classList.remove('cart-drawer-open');
    doc.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });

    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 340);
  }

  async function getCart() {
    const response = await fetch(`${routeRoot}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('A kosár nem tölthető be.');
    return response.json();
  }

  function updateCartCount(cart) {
    doc.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
    });
  }

  async function refreshCartDrawer() {
    const currentDrawer = getDrawer();
    if (!currentDrawer) return;

    const response = await fetch(`${routeRoot}cart?section_id=cart-drawer`, {
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('A kosárpanel nem frissíthető.');

    const html = await response.text();
    const template = doc.createElement('template');
    template.innerHTML = html.trim();
    const nextDrawer = template.content.querySelector('[data-cart-drawer]');

    if (nextDrawer) {
      currentDrawer.replaceWith(nextDrawer);
    }
  }

  async function addToCart(form, submitter) {
    const button = submitter || form.querySelector('[data-add-to-cart]');
    const buttonText = button?.querySelector('[data-add-to-cart-text]');
    const originalText = buttonText?.textContent;

    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    form.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${routeRoot}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.description || result.message || 'A termék nem helyezhető a kosárba.');
      }

      const cart = await getCart();
      updateCartCount(cart);

      try {
        await refreshCartDrawer();
      } catch (drawerError) {
        // Cart page remains the safe fallback if section rendering is unavailable.
      }

      if (!openDrawer()) {
        window.location.assign(`${routeRoot}cart`);
        return;
      }

      announce('A termék bekerült a kosárba.');
      doc.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      doc.dispatchEvent(new CustomEvent('product:added', { detail: { product: result } }));
    } catch (error) {
      announce(error.message);
      if (buttonText) buttonText.textContent = error.message;
      window.setTimeout(() => {
        if (buttonText && originalText) buttonText.textContent = originalText;
      }, 2800);
    } finally {
      button?.classList.remove('is-loading');
      if (button) button.disabled = false;
      form.removeAttribute('aria-busy');
    }
  }

  function initProduct(section) {
    const variantsScript = section.querySelector('[data-product-variants]');
    const select = section.querySelector('[data-variant-select]');
    if (!variantsScript || !select) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    select.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;

      section.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
      });

      const price = section.querySelector('[data-product-price]');
      const compare = section.querySelector('[data-product-compare-price]');
      const inventory = section.querySelector('[data-product-inventory]');
      const addButton = section.querySelector('[data-add-to-cart]');
      const addText = section.querySelector('[data-add-to-cart-text]');

      if (price) price.textContent = formatMoney(variant.price);

      if (compare) {
        const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
        compare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
        compare.classList.toggle('is-hidden', !hasCompare);
      }

      if (inventory) {
        inventory.textContent = variant.available ? 'Raktáron' : 'Elfogyott';
        inventory.classList.toggle('is-available', variant.available);
        inventory.classList.toggle('is-soldout', !variant.available);
      }

      if (addButton) addButton.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? 'Kosárba teszem' : 'Elfogyott';

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      if (variant.featured_media && variant.featured_media.id) {
        const media = section.querySelector(`[data-product-media="${variant.featured_media.id}"]`);
        media?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  doc.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !getDrawer()) return;
    event.preventDefault();
    addToCart(form, event.submitter);
  });

  doc.addEventListener('click', (event) => {
    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeDrawer();
      return;
    }

    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail) {
      const section = thumbnail.closest('[data-main-product]');
      const media = section?.querySelector(`[data-product-media="${thumbnail.dataset.mediaTarget}"]`);
      section?.querySelectorAll('[data-media-target]').forEach((item) => item.classList.remove('is-active'));
      thumbnail.classList.add('is-active');
      media?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const copyButton = event.target.closest('[data-copy-link]');
    if (copyButton) {
      navigator.clipboard
        ?.writeText(copyButton.dataset.url || window.location.href)
        .then(() => announce('A linket kimásoltuk.'));
    }
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('cart-drawer-open')) {
      closeDrawer();
    }
  });

  doc.querySelectorAll('[data-main-product]').forEach(initProduct);

  doc.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-main-product]').forEach(initProduct);
  });
})();
